/**
 * SchoolFrontOffice — Full Front Office / Reception Module
 * Tabs: Dashboard, Enquiries, Visitors, Appointments, Complaints,
 *       Gate Passes, Document Requests, Parcels, Desk Log
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  LayoutDashboard, Search, Plus, Loader2, X, Check, Eye, Edit, Clock,
  Users, Phone, Mail, MessageSquare, FileText, Package, Shield, CalendarDays,
  AlertTriangle, DoorOpen, ClipboardList, BookOpen, ArrowRight, UserCheck,
  ChevronDown, MoreHorizontal, LogIn, LogOut, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { getToken } from '@/lib/authToken';
import { useCampusContext, CampusHolidayBanner } from '@/components/school/CampusContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts, headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString(); } catch { return d; } };
const fmtTime = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

const STATUS_BADGE = {
  new: 'bg-blue-100 text-blue-800', in_progress: 'bg-amber-100 text-amber-800', awaiting_response: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800', closed: 'bg-slate-100 text-slate-700',
  expected: 'bg-blue-100 text-blue-800', checked_in: 'bg-green-100 text-green-800', checked_out: 'bg-slate-100 text-slate-700',
  denied: 'bg-red-100 text-red-800', expired: 'bg-slate-100 text-slate-500',
  scheduled: 'bg-blue-100 text-blue-800', confirmed: 'bg-indigo-100 text-indigo-800', completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800', missed: 'bg-orange-100 text-orange-800',
  open: 'bg-red-100 text-red-800', assigned: 'bg-amber-100 text-amber-800', under_review: 'bg-purple-100 text-purple-800',
  draft: 'bg-slate-100 text-slate-600', approved: 'bg-green-100 text-green-800', issued: 'bg-blue-100 text-blue-800',
  used: 'bg-slate-100 text-slate-700',
  requested: 'bg-blue-100 text-blue-800', processing: 'bg-amber-100 text-amber-800', ready: 'bg-green-100 text-green-800',
  collected: 'bg-slate-100 text-slate-700', sent: 'bg-indigo-100 text-indigo-800',
  received: 'bg-blue-100 text-blue-800', notified: 'bg-amber-100 text-amber-800', returned: 'bg-red-100 text-red-800',
};

// ============== DASHBOARD ==============
const FODashboard = ({ tenantId, setTab }) => {
  const { data: d, isLoading } = useQuery({ queryKey: ['fo-dashboard', tenantId], queryFn: () => api(`/tenants/${tenantId}/front-office/dashboard`) });
  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  const dash = d || {};
  const stats = [
    { label: 'Appointments Today', value: dash.today_appointments, icon: CalendarDays, color: 'text-blue-600 bg-blue-50' },
    { label: 'New Enquiries', value: dash.enquiries_new, icon: MessageSquare, color: 'text-indigo-600 bg-indigo-50' },
    { label: 'Visitors In', value: dash.visitors_checked_in, icon: Users, color: 'text-green-600 bg-green-50' },
    { label: 'Open Complaints', value: dash.complaints_open, icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
    { label: 'Active Gate Passes', value: dash.gate_passes_active, icon: DoorOpen, color: 'text-amber-600 bg-amber-50' },
    { label: 'Doc Requests', value: dash.doc_requests_pending, icon: FileText, color: 'text-purple-600 bg-purple-50' },
    { label: 'Parcels Awaiting', value: dash.parcels_awaiting, icon: Package, color: 'text-teal-600 bg-teal-50' },
    { label: 'Visitors Today', value: dash.visitors_today, icon: UserCheck, color: 'text-cyan-600 bg-cyan-50' },
  ];
  const quickActions = [
    { label: 'New Enquiry', tab: 'enquiries', icon: MessageSquare },
    { label: 'New Visitor', tab: 'visitors', icon: Users },
    { label: 'Book Appointment', tab: 'appointments', icon: CalendarDays },
    { label: 'Issue Gate Pass', tab: 'gate_passes', icon: DoorOpen },
    { label: 'Log Complaint', tab: 'complaints', icon: AlertTriangle },
    { label: 'Record Parcel', tab: 'parcels', icon: Package },
    { label: 'Document Request', tab: 'documents', icon: FileText },
  ];
  return (
    <div className="space-y-6" data-testid="fo-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map(s => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${s.color}`}><s.icon className="h-4 w-4" /></div>
              <div><p className="text-2xl font-bold text-slate-900">{s.value ?? 0}</p><p className="text-xs text-slate-500">{s.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Quick Actions</CardTitle></CardHeader>
        <CardContent><div className="flex flex-wrap gap-2">
          {quickActions.map(a => (<Button key={a.tab} variant="outline" size="sm" onClick={() => setTab(a.tab)} data-testid={`qa-${a.tab}`}><a.icon className="h-4 w-4 mr-1" />{a.label}</Button>))}
        </div></CardContent>
      </Card>
      {(dash.recent_actions?.length > 0) && (
        <Card>
          <CardHeader><CardTitle className="text-base">Recent Activity</CardTitle></CardHeader>
          <CardContent><div className="space-y-2 max-h-48 overflow-y-auto">
            {dash.recent_actions.map((a, i) => (
              <div key={a.id || i} className="flex items-center gap-2 text-sm border-b border-slate-50 pb-1">
                <Badge variant="outline" className="text-xs shrink-0 capitalize">{a.entity_type}</Badge>
                <span className="text-slate-600 capitalize">{(a.action || '').replace('_', ' ')}</span>
                {a.details && <span className="text-slate-400 truncate">&mdash; {a.details}</span>}
                <span className="text-xs text-slate-400 ml-auto shrink-0">{fmtTime(a.created_at)}</span>
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== GENERIC CRUD TAB (reusable) ==============
const CrudTab = ({ tenantId, config }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data: items = [], isLoading } = useQuery({
    queryKey: [config.queryKey, tenantId, statusFilter, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      const qs = params.toString();
      return api(`/tenants/${tenantId}/front-office/${config.endpoint}${qs ? '?' + qs : ''}`);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/front-office/${config.endpoint}`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (d) => { toast.success(`${config.label} created${d[config.idField] ? ` (${d[config.idField]})` : ''}`); closeForm(); queryClient.invalidateQueries({ queryKey: [config.queryKey] }); queryClient.invalidateQueries({ queryKey: ['fo-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api(`/tenants/${tenantId}/front-office/${config.endpoint}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success(`${config.label} updated`); closeForm(); queryClient.invalidateQueries({ queryKey: [config.queryKey] }); queryClient.invalidateQueries({ queryKey: ['fo-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const closeForm = () => { setShowForm(false); setEditing(null); setForm({}); };
  const openNew = () => { setEditing(null); setForm(config.defaultForm()); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...config.defaultForm(), ...item }); setShowForm(true); };

  return (
    <div className="space-y-4" data-testid={`fo-${config.endpoint}`}>
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 items-center flex-wrap">
          {config.searchable && (
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 w-48" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} data-testid={`${config.endpoint}-search`} />
            </div>
          )}
          {config.statuses.length > 0 && (
            <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {config.statuses.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button onClick={openNew} data-testid={`add-${config.endpoint}-btn`}><Plus className="h-4 w-4 mr-1" /> {config.addLabel}</Button>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>{config.columns.map(c => <TableHead key={c.key} className={c.className}>{c.label}</TableHead>)}<TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={config.columns.length + 1} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={config.columns.length + 1} className="text-center py-8 text-slate-400">No records found</TableCell></TableRow>
            ) : items.map(item => (
              <TableRow key={item.id}>
                {config.columns.map(c => <TableCell key={c.key} className={c.className}>{c.render ? c.render(item) : item[c.key] || '-'}</TableCell>)}
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(item)}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      {config.extraActions?.(item, queryClient, tenantId)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={v => !v && closeForm()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={`${config.endpoint}-form-desc`}>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${config.label}` : config.addLabel}</DialogTitle>
            <p id={`${config.endpoint}-form-desc`} className="sr-only">{config.label} form</p>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.formFields.map(f => (
              <div key={f.key} className={f.fullWidth ? 'col-span-full' : ''}>
                <Label>{f.label}</Label>
                {f.type === 'select' ? (
                  <Select value={form[f.key] || ''} onValueChange={v => setForm({...form, [f.key]: v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{f.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === 'textarea' ? (
                  <Textarea value={form[f.key] || ''} onChange={e => setForm({...form, [f.key]: e.target.value})} />
                ) : (
                  <Input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm({...form, [f.key]: e.target.value})} data-testid={`${config.endpoint}-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => editing ? updateMutation.mutate({ id: editing.id, data: form }) : createMutation.mutate(form)}
              disabled={createMutation.isPending || updateMutation.isPending} data-testid={`save-${config.endpoint}-btn`}>
              {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== VISITORS TAB (Custom for check-in/out) ==============
const VisitorsTab = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data: visitors = [], isLoading } = useQuery({
    queryKey: ['fo-visitors', tenantId, statusFilter, search],
    queryFn: () => {
      const p = new URLSearchParams();
      if (statusFilter) p.set('status', statusFilter);
      if (search) p.set('search', search);
      return api(`/tenants/${tenantId}/front-office/visitors?${p}`);
    },
  });

  const emptyForm = { name: '', visitor_type: 'guest', id_number: '', phone: '', email: '', person_visiting_name: '', office_visiting: '', purpose: '', vehicle_reg: '', notes: '', approval_status: 'expected', linked_student_id: '' };

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/front-office/visitors`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (d) => { toast.success(`Visitor registered! Pass: ${d.pass_code}`); setShowForm(false); queryClient.invalidateQueries({ queryKey: ['fo-visitors'] }); queryClient.invalidateQueries({ queryKey: ['fo-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api(`/tenants/${tenantId}/front-office/visitors/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Visitor updated'); setShowForm(false); setEditing(null); queryClient.invalidateQueries({ queryKey: ['fo-visitors'] }); queryClient.invalidateQueries({ queryKey: ['fo-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const checkinMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/front-office/visitors/${id}/checkin`, { method: 'POST' }),
    onSuccess: () => { toast.success('Visitor checked in'); queryClient.invalidateQueries({ queryKey: ['fo-visitors'] }); queryClient.invalidateQueries({ queryKey: ['fo-dashboard'] }); },
  });

  const checkoutMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/front-office/visitors/${id}/checkout`, { method: 'POST' }),
    onSuccess: () => { toast.success('Visitor checked out'); queryClient.invalidateQueries({ queryKey: ['fo-visitors'] }); queryClient.invalidateQueries({ queryKey: ['fo-dashboard'] }); },
  });

  const VIS_TYPES = ['parent', 'supplier', 'guest', 'official', 'contractor', 'applicant'];
  const VIS_STATUSES = ['expected', 'checked_in', 'checked_out', 'denied', 'expired'];

  return (
    <div className="space-y-4" data-testid="fo-visitors">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input className="pl-9 w-48" placeholder="Search visitors..." value={search} onChange={e => setSearch(e.target.value)} data-testid="visitors-search" /></div>
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{VIS_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setShowForm(true); }} data-testid="add-visitor-btn"><Plus className="h-4 w-4 mr-1" /> Register Visitor</Button>
      </div>

      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Visitor</TableHead><TableHead>Type</TableHead><TableHead>Visiting</TableHead><TableHead>Purpose</TableHead><TableHead>Time In/Out</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : visitors.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No visitors found</TableCell></TableRow>
            ) : visitors.map(v => (
              <TableRow key={v.id}>
                <TableCell>
                  <p className="font-medium">{v.name}</p>
                  <p className="text-xs text-slate-500">{v.visitor_id} {v.phone ? `| ${v.phone}` : ''}</p>
                </TableCell>
                <TableCell><Badge variant="outline" className="capitalize text-xs">{(v.visitor_type || '').replace('_', ' ')}</Badge></TableCell>
                <TableCell className="text-sm">{v.person_visiting_name || v.office_visiting || '-'}</TableCell>
                <TableCell className="text-sm max-w-[150px] truncate">{v.purpose || '-'}</TableCell>
                <TableCell className="text-sm">{v.entry_time || '-'} / {v.exit_time || '-'}</TableCell>
                <TableCell><Badge className={`text-xs ${STATUS_BADGE[v.approval_status] || ''}`}>{(v.approval_status || '').replace('_', ' ')}</Badge></TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {v.approval_status === 'expected' && <DropdownMenuItem onClick={() => checkinMutation.mutate(v.id)}><LogIn className="h-4 w-4 mr-2 text-green-600" /> Check In</DropdownMenuItem>}
                      {v.approval_status === 'checked_in' && <DropdownMenuItem onClick={() => checkoutMutation.mutate(v.id)}><LogOut className="h-4 w-4 mr-2 text-blue-600" /> Check Out</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => { setEditing(v); setForm({ ...emptyForm, ...v }); setShowForm(true); }}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={v => !v && setShowForm(false)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="visitor-form-desc">
          <DialogHeader><DialogTitle>{editing ? 'Edit Visitor' : 'Register Visitor'}</DialogTitle><p id="visitor-form-desc" className="sr-only">Visitor form</p></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} data-testid="visitor-name-input" /></div>
            <div><Label>Type</Label><Select value={form.visitor_type || ''} onValueChange={v => setForm({...form, visitor_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{VIS_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Phone</Label><Input value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div><Label>ID / Passport</Label><Input value={form.id_number || ''} onChange={e => setForm({...form, id_number: e.target.value})} /></div>
            <div><Label>Person Visiting</Label><Input value={form.person_visiting_name || ''} onChange={e => setForm({...form, person_visiting_name: e.target.value})} /></div>
            <div><Label>Office / Department</Label><Input value={form.office_visiting || ''} onChange={e => setForm({...form, office_visiting: e.target.value})} /></div>
            <div className="col-span-full"><Label>Purpose</Label><Textarea value={form.purpose || ''} onChange={e => setForm({...form, purpose: e.target.value})} /></div>
            <div><Label>Vehicle Reg</Label><Input value={form.vehicle_reg || ''} onChange={e => setForm({...form, vehicle_reg: e.target.value})} /></div>
            <div><Label>Email</Label><Input value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} /></div>
          </div>
          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={() => editing ? updateMutation.mutate({ id: editing.id, data: form }) : createMutation.mutate(form)} disabled={!form.name || createMutation.isPending || updateMutation.isPending} data-testid="save-visitor-btn">{(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{editing ? 'Update' : 'Register'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== TAB CONFIGS ==============
const ENQUIRY_CONFIG = {
  queryKey: 'fo-enquiries', endpoint: 'enquiries', label: 'Enquiry', addLabel: 'New Enquiry', idField: 'enquiry_id', searchable: true,
  statuses: ['new', 'in_progress', 'awaiting_response', 'resolved', 'closed'],
  columns: [
    { key: 'enquiry_id', label: 'ID', render: (r) => <><p className="font-medium">{r.enquiry_id}</p><p className="text-xs text-slate-500">{r.name}</p></> },
    { key: 'enquiry_type', label: 'Type', render: (r) => <Badge variant="outline" className="capitalize text-xs">{(r.enquiry_type || '').replace('_', ' ')}</Badge> },
    { key: 'source', label: 'Source', render: (r) => <span className="text-sm capitalize">{(r.source || '').replace('_', ' ')}</span> },
    { key: 'priority', label: 'Priority', render: (r) => <Badge className={r.priority === 'urgent' ? 'bg-red-100 text-red-800' : r.priority === 'high' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'} >{r.priority}</Badge> },
    { key: 'status', label: 'Status', render: (r) => <Badge className={`text-xs ${STATUS_BADGE[r.status] || ''}`}>{(r.status || '').replace('_', ' ')}</Badge> },
    { key: 'created_at', label: 'Date', render: (r) => <span className="text-sm">{fmtDate(r.created_at)}</span> },
  ],
  defaultForm: () => ({ name: '', phone: '', email: '', enquiry_type: 'general', source: 'walk_in', message: '', priority: 'normal', status: 'new', follow_up_date: '' }),
  formFields: [
    { key: 'name', label: 'Name *' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'enquiry_type', label: 'Type', type: 'select', options: [{ value: 'admission', label: 'Admission' }, { value: 'general', label: 'General' }, { value: 'fees', label: 'Fees' }, { value: 'academic', label: 'Academic' }, { value: 'boarding', label: 'Boarding' }, { value: 'transport', label: 'Transport' }] },
    { key: 'source', label: 'Source', type: 'select', options: [{ value: 'walk_in', label: 'Walk-in' }, { value: 'phone', label: 'Phone' }, { value: 'email', label: 'Email' }, { value: 'website', label: 'Website' }, { value: 'referral', label: 'Referral' }] },
    { key: 'priority', label: 'Priority', type: 'select', options: [{ value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' }] },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: 'new', label: 'New' }, { value: 'in_progress', label: 'In Progress' }, { value: 'awaiting_response', label: 'Awaiting Response' }, { value: 'resolved', label: 'Resolved' }, { value: 'closed', label: 'Closed' }] },
    { key: 'follow_up_date', label: 'Follow-up Date', type: 'date' },
    { key: 'message', label: 'Message / Details', type: 'textarea', fullWidth: true },
  ],
};

const APPOINTMENT_CONFIG = {
  queryKey: 'fo-appointments', endpoint: 'appointments', label: 'Appointment', addLabel: 'Book Appointment', idField: 'appointment_id', searchable: false,
  statuses: ['scheduled', 'confirmed', 'completed', 'cancelled', 'missed'],
  columns: [
    { key: 'appointment_id', label: 'ID', render: (r) => <><p className="font-medium">{r.appointment_id}</p><p className="text-xs text-slate-500">{r.title}</p></> },
    { key: 'appointment_type', label: 'Type', render: (r) => <Badge variant="outline" className="capitalize text-xs">{(r.appointment_type || '').replace('_', ' ')}</Badge> },
    { key: 'date', label: 'Date / Time', render: (r) => <><span className="text-sm">{r.date}</span>{r.time && <span className="text-xs text-slate-500 ml-1">{r.time}</span>}</> },
    { key: 'staff_name', label: 'With', render: (r) => <span className="text-sm">{r.staff_name || r.visitor_name || '-'}</span> },
    { key: 'status', label: 'Status', render: (r) => <Badge className={`text-xs ${STATUS_BADGE[r.status] || ''}`}>{(r.status || '').replace('_', ' ')}</Badge> },
  ],
  defaultForm: () => ({ title: '', appointment_type: 'general', date: new Date().toISOString().split('T')[0], time: '', location: '', purpose: '', visitor_name: '', visitor_phone: '', status: 'scheduled', notes: '' }),
  formFields: [
    { key: 'title', label: 'Title *' },
    { key: 'appointment_type', label: 'Type', type: 'select', options: [{ value: 'admissions', label: 'Admissions' }, { value: 'disciplinary', label: 'Disciplinary' }, { value: 'welfare', label: 'Welfare' }, { value: 'counselling', label: 'Counselling' }, { value: 'admin', label: 'Admin' }, { value: 'parent_meeting', label: 'Parent Meeting' }, { value: 'general', label: 'General' }] },
    { key: 'date', label: 'Date *', type: 'date' },
    { key: 'time', label: 'Time', type: 'time' },
    { key: 'location', label: 'Location' },
    { key: 'visitor_name', label: 'Visitor Name' },
    { key: 'visitor_phone', label: 'Visitor Phone' },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: 'scheduled', label: 'Scheduled' }, { value: 'confirmed', label: 'Confirmed' }, { value: 'completed', label: 'Completed' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'missed', label: 'Missed' }] },
    { key: 'purpose', label: 'Purpose', type: 'textarea', fullWidth: true },
  ],
};

const COMPLAINT_CONFIG = {
  queryKey: 'fo-complaints', endpoint: 'complaints', label: 'Complaint', addLabel: 'Log Complaint', idField: 'complaint_id', searchable: false,
  statuses: ['open', 'assigned', 'under_review', 'resolved', 'closed'],
  columns: [
    { key: 'complaint_id', label: 'ID', render: (r) => <><p className="font-medium">{r.complaint_id}</p><p className="text-xs text-slate-500">{r.complainant_name}</p></> },
    { key: 'category', label: 'Category', render: (r) => <Badge variant="outline" className="capitalize text-xs">{(r.category || '').replace('_', ' ')}</Badge> },
    { key: 'severity', label: 'Severity', render: (r) => <Badge className={r.severity === 'critical' ? 'bg-red-100 text-red-800' : r.severity === 'high' ? 'bg-amber-100 text-amber-800' : r.severity === 'medium' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}>{r.severity}</Badge> },
    { key: 'status', label: 'Status', render: (r) => <Badge className={`text-xs ${STATUS_BADGE[r.status] || ''}`}>{(r.status || '').replace('_', ' ')}</Badge> },
    { key: 'created_at', label: 'Date', render: (r) => <span className="text-sm">{fmtDate(r.created_at)}</span> },
  ],
  defaultForm: () => ({ complainant_name: '', complainant_type: 'parent', category: 'general', details: '', severity: 'medium', status: 'open', due_date: '', resolution_notes: '' }),
  formFields: [
    { key: 'complainant_name', label: 'Complainant Name *' },
    { key: 'complainant_type', label: 'Type', type: 'select', options: [{ value: 'parent', label: 'Parent' }, { value: 'student', label: 'Student' }, { value: 'staff', label: 'Staff' }, { value: 'visitor', label: 'Visitor' }, { value: 'other', label: 'Other' }] },
    { key: 'category', label: 'Category', type: 'select', options: [{ value: 'academic', label: 'Academic' }, { value: 'discipline', label: 'Discipline' }, { value: 'facility', label: 'Facility' }, { value: 'service', label: 'Service' }, { value: 'safety', label: 'Safety' }, { value: 'fees', label: 'Fees' }, { value: 'transport', label: 'Transport' }, { value: 'general', label: 'General' }, { value: 'other', label: 'Other' }] },
    { key: 'severity', label: 'Severity', type: 'select', options: [{ value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' }] },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: 'open', label: 'Open' }, { value: 'assigned', label: 'Assigned' }, { value: 'under_review', label: 'Under Review' }, { value: 'resolved', label: 'Resolved' }, { value: 'closed', label: 'Closed' }] },
    { key: 'due_date', label: 'Due Date', type: 'date' },
    { key: 'details', label: 'Details', type: 'textarea', fullWidth: true },
    { key: 'resolution_notes', label: 'Resolution Notes', type: 'textarea', fullWidth: true },
  ],
};

const GATE_PASS_CONFIG = {
  queryKey: 'fo-gate-passes', endpoint: 'gate-passes', label: 'Gate Pass', addLabel: 'Issue Gate Pass', idField: 'pass_id', searchable: false,
  statuses: ['draft', 'approved', 'issued', 'used', 'expired', 'cancelled'],
  columns: [
    { key: 'pass_id', label: 'Pass ID', render: (r) => <><p className="font-medium">{r.pass_id}</p><p className="text-xs text-slate-500">{r.person_name || r.student_name || '-'}</p></> },
    { key: 'pass_type', label: 'Type', render: (r) => <Badge variant="outline" className="capitalize text-xs">{(r.pass_type || '').replace('_', ' ')}</Badge> },
    { key: 'reason', label: 'Reason', render: (r) => <span className="text-sm max-w-[200px] truncate block">{r.reason || '-'}</span> },
    { key: 'valid_from', label: 'Valid', render: (r) => <span className="text-sm">{r.valid_from || '-'} {r.valid_to ? `to ${r.valid_to}` : ''}</span> },
    { key: 'status', label: 'Status', render: (r) => <Badge className={`text-xs ${STATUS_BADGE[r.status] || ''}`}>{(r.status || '').replace('_', ' ')}</Badge> },
  ],
  defaultForm: () => ({ pass_type: 'student_exit', person_name: '', reason: '', destination: '', valid_from: new Date().toISOString().split('T')[0], valid_to: '', status: 'draft', notes: '' }),
  formFields: [
    { key: 'pass_type', label: 'Type', type: 'select', options: [{ value: 'student_exit', label: 'Student Exit' }, { value: 'parent_pickup', label: 'Parent Pickup' }, { value: 'boarding_pass', label: 'Boarding Pass' }, { value: 'visitor_pass', label: 'Visitor Pass' }, { value: 'temp_leave', label: 'Temporary Leave' }] },
    { key: 'person_name', label: 'Person Name *' },
    { key: 'destination', label: 'Destination' },
    { key: 'valid_from', label: 'Valid From', type: 'date' },
    { key: 'valid_to', label: 'Valid To', type: 'date' },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: 'draft', label: 'Draft' }, { value: 'approved', label: 'Approved' }, { value: 'issued', label: 'Issued' }, { value: 'used', label: 'Used' }, { value: 'expired', label: 'Expired' }, { value: 'cancelled', label: 'Cancelled' }] },
    { key: 'reason', label: 'Reason', type: 'textarea', fullWidth: true },
  ],
};

const DOC_REQUEST_CONFIG = {
  queryKey: 'fo-doc-requests', endpoint: 'document-requests', label: 'Document Request', addLabel: 'New Request', idField: 'request_id', searchable: false,
  statuses: ['requested', 'processing', 'ready', 'collected', 'sent', 'cancelled'],
  columns: [
    { key: 'request_id', label: 'ID', render: (r) => <><p className="font-medium">{r.request_id}</p><p className="text-xs text-slate-500">{r.requester_name || r.student_name || '-'}</p></> },
    { key: 'document_type', label: 'Document', render: (r) => <Badge variant="outline" className="capitalize text-xs">{(r.document_type || '').replace('_', ' ')}</Badge> },
    { key: 'status', label: 'Status', render: (r) => <Badge className={`text-xs ${STATUS_BADGE[r.status] || ''}`}>{(r.status || '').replace('_', ' ')}</Badge> },
    { key: 'created_at', label: 'Requested', render: (r) => <span className="text-sm">{fmtDate(r.created_at)}</span> },
  ],
  defaultForm: () => ({ document_type: 'enrollment_letter', requester_name: '', requester_phone: '', reason: '', status: 'requested', notes: '' }),
  formFields: [
    { key: 'document_type', label: 'Document Type *', type: 'select', options: [{ value: 'enrollment_letter', label: 'Enrollment Letter' }, { value: 'transfer_letter', label: 'Transfer Letter' }, { value: 'recommendation', label: 'Recommendation Letter' }, { value: 'fee_statement', label: 'Fee Statement' }, { value: 'exam_results', label: 'Exam Results' }, { value: 'boarding_confirmation', label: 'Boarding Confirmation' }, { value: 'other', label: 'Other' }] },
    { key: 'requester_name', label: 'Requester Name *' },
    { key: 'requester_phone', label: 'Phone' },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: 'requested', label: 'Requested' }, { value: 'processing', label: 'Processing' }, { value: 'ready', label: 'Ready' }, { value: 'collected', label: 'Collected' }, { value: 'sent', label: 'Sent' }, { value: 'cancelled', label: 'Cancelled' }] },
    { key: 'reason', label: 'Reason', type: 'textarea', fullWidth: true },
  ],
};

const PARCEL_CONFIG = {
  queryKey: 'fo-parcels', endpoint: 'parcels', label: 'Parcel', addLabel: 'Record Parcel', idField: 'parcel_id', searchable: false,
  statuses: ['received', 'notified', 'collected', 'returned'],
  columns: [
    { key: 'parcel_id', label: 'ID', render: (r) => <><p className="font-medium">{r.parcel_id}</p><p className="text-xs text-slate-500">{r.tracking_number || '-'}</p></> },
    { key: 'sender', label: 'From', render: (r) => <span className="text-sm">{r.sender || '-'}</span> },
    { key: 'recipient_name', label: 'To', render: (r) => <><p className="text-sm font-medium">{r.recipient_name}</p><p className="text-xs text-slate-500 capitalize">{r.recipient_type}</p></> },
    { key: 'parcel_type', label: 'Type', render: (r) => <Badge variant="outline" className="capitalize text-xs">{r.parcel_type}</Badge> },
    { key: 'status', label: 'Status', render: (r) => <Badge className={`text-xs ${STATUS_BADGE[r.status] || ''}`}>{(r.status || '').replace('_', ' ')}</Badge> },
  ],
  defaultForm: () => ({ sender: '', recipient_name: '', recipient_type: 'student', parcel_type: 'package', courier_company: '', tracking_number: '', status: 'received', notes: '' }),
  formFields: [
    { key: 'sender', label: 'Sender' },
    { key: 'recipient_name', label: 'Recipient *' },
    { key: 'recipient_type', label: 'Recipient Type', type: 'select', options: [{ value: 'student', label: 'Student' }, { value: 'staff', label: 'Staff' }, { value: 'department', label: 'Department' }] },
    { key: 'parcel_type', label: 'Parcel Type', type: 'select', options: [{ value: 'package', label: 'Package' }, { value: 'letter', label: 'Letter' }, { value: 'courier', label: 'Courier' }, { value: 'other', label: 'Other' }] },
    { key: 'courier_company', label: 'Courier Company' },
    { key: 'tracking_number', label: 'Tracking Number' },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: 'received', label: 'Received' }, { value: 'notified', label: 'Notified' }, { value: 'collected', label: 'Collected' }, { value: 'returned', label: 'Returned' }] },
    { key: 'notes', label: 'Notes', type: 'textarea', fullWidth: true },
  ],
};

const DESK_LOG_CONFIG = {
  queryKey: 'fo-desk-log', endpoint: 'desk-log', label: 'Log Entry', addLabel: 'New Log Entry', idField: 'id', searchable: false,
  statuses: [],
  columns: [
    { key: 'log_type', label: 'Type', render: (r) => <Badge variant="outline" className="capitalize text-xs">{(r.log_type || '').replace('_', ' ')}</Badge> },
    { key: 'caller_name', label: 'Caller/Visitor', render: (r) => <><p className="text-sm font-medium">{r.caller_name || '-'}</p>{r.phone && <p className="text-xs text-slate-500">{r.phone}</p>}</> },
    { key: 'message', label: 'Message', render: (r) => <span className="text-sm max-w-[250px] truncate block">{r.message || '-'}</span> },
    { key: 'intended_for_name', label: 'For', render: (r) => <span className="text-sm">{r.intended_for_name || '-'}</span> },
    { key: 'created_at', label: 'Time', render: (r) => <span className="text-sm">{fmtTime(r.created_at)}</span> },
  ],
  defaultForm: () => ({ log_type: 'incoming_call', caller_name: '', phone: '', message: '', intended_for_name: '', action_taken: '', priority: 'normal' }),
  formFields: [
    { key: 'log_type', label: 'Type', type: 'select', options: [{ value: 'incoming_call', label: 'Incoming Call' }, { value: 'outgoing_call', label: 'Outgoing Call' }, { value: 'walk_in', label: 'Walk-in' }, { value: 'message', label: 'Message' }, { value: 'instruction', label: 'Instruction' }] },
    { key: 'caller_name', label: 'Caller / Visitor Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'intended_for_name', label: 'Intended For' },
    { key: 'priority', label: 'Priority', type: 'select', options: [{ value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' }] },
    { key: 'message', label: 'Message', type: 'textarea', fullWidth: true },
    { key: 'action_taken', label: 'Action Taken', type: 'textarea', fullWidth: true },
  ],
};


// ============== MAIN EXPORT ==============
export const SchoolFrontOffice = ({ tenantId }) => {
  const [tab, setTab] = useState('dashboard');
  const { campusId, campuses } = useCampusContext(tenantId);
  const currentCampus = campuses.find(c => c.id === campusId);

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'enquiries', label: 'Enquiries', icon: MessageSquare },
    { id: 'visitors', label: 'Visitors', icon: Users },
    { id: 'appointments', label: 'Appointments', icon: CalendarDays },
    { id: 'complaints', label: 'Complaints', icon: AlertTriangle },
    { id: 'gate_passes', label: 'Gate Passes', icon: DoorOpen },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'parcels', label: 'Parcels', icon: Package },
    { id: 'desk_log', label: 'Desk Log', icon: ClipboardList },
  ];

  return (
    <div className="space-y-4" data-testid="school-front-office">
      <CampusHolidayBanner tenantId={tenantId} />
      {campusId && currentCampus && (
        <div className="text-xs text-slate-500" data-testid="fo-campus-scope">
          Front office actions scoped to <b>{currentCampus.name}</b>. Switch the campus chip at the top to change.
        </div>
      )}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {TABS.map(t => (
          <Button key={t.id} variant={tab === t.id ? 'default' : 'ghost'} size="sm" onClick={() => setTab(t.id)} className="shrink-0" data-testid={`fo-tab-${t.id}`}>
            <t.icon className="h-4 w-4 mr-1" />{t.label}
          </Button>
        ))}
      </div>
      {tab === 'dashboard' && <FODashboard tenantId={tenantId} setTab={setTab} />}
      {tab === 'enquiries' && <CrudTab tenantId={tenantId} config={ENQUIRY_CONFIG} />}
      {tab === 'visitors' && <VisitorsTab tenantId={tenantId} />}
      {tab === 'appointments' && <CrudTab tenantId={tenantId} config={APPOINTMENT_CONFIG} />}
      {tab === 'complaints' && <CrudTab tenantId={tenantId} config={COMPLAINT_CONFIG} />}
      {tab === 'gate_passes' && <CrudTab tenantId={tenantId} config={GATE_PASS_CONFIG} />}
      {tab === 'documents' && <CrudTab tenantId={tenantId} config={DOC_REQUEST_CONFIG} />}
      {tab === 'parcels' && <CrudTab tenantId={tenantId} config={PARCEL_CONFIG} />}
      {tab === 'desk_log' && <CrudTab tenantId={tenantId} config={DESK_LOG_CONFIG} />}
    </div>
  );
};

export default SchoolFrontOffice;
